/**
 * Supabase client configuration for the admin dashboard.
 * 
 * SECURITY: This client uses the anonymous key only. All admin access
 * is controlled via RLS policies that check the admin_users table.
 * Never expose the service role key in frontend code.
 * 
 * @module lib/supabase
 */

import { createClient } from '@supabase/supabase-js';
import type { Database } from '../types/database';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

/**
 * No-op auth lock to avoid AbortError from Navigator LockManager in some environments.
 *
 * @param _name - Lock name (unused)
 * @param _timeout - Lock acquire timeout (unused)
 * @param fn - Operation to execute
 * @returns Result of the operation
 */
const noOpAuthLock = async <T,>(
  _name: string,
  _timeout: number,
  fn: () => Promise<T>
): Promise<T> => fn();

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing Supabase environment variables. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.'
  );
}

/**
 * Supabase client instance configured with type safety.
 * All queries go through RLS policies - admin access is verified
 * by the is_admin() function in the database.
 */
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    lock: noOpAuthLock,
  },
  global: {
    fetch: (url, options = {}) => {
      // Add timeout to prevent hanging requests
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      return fetch(url, { ...options, signal: controller.signal })
        .then(response => {
          clearTimeout(timeoutId);
          return response;
        })
        .catch(error => {
          clearTimeout(timeoutId);
          if (error.name === 'AbortError') {
            throw new Error('Request timed out');
          }
          throw error;
        });
    }
  }
});
